import SearchIcon from '@mui/icons-material/Search';
import {
  FormControl,
  FormHelperText,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Paper,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { Formik } from 'formik';
import { OptionsObject, SnackbarKey } from 'notistack';
import { useMemo, useState } from 'react';
import HighlightWithinTextarea from 'react-highlight-within-textarea';
import { useMutation } from 'react-query';
import { SetterOrUpdater, useRecoilStateLoadable } from 'recoil';
import * as Yup from 'yup';

import { LabelledOutline } from 'src/common/final-form-material-ui';

import { FormikTextField } from '../../../common/formik-material-ui';
import { trackSaveAddSMSTemplate } from '../../../common/userTracking';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import { IAlertWithProgressBar } from '../notifications/SnackbarProviderWithStyles';

import { ModalWithStyles } from './ModalWithStyles';

const useStyles = makeStyles((theme: Theme) => ({
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  contentField: {
    height: '150px',
    padding: '10.5px 14px',
    borderRadius: '6px',
    '& [data-contents="true"]': {
      height: '91px',
      overflowY: 'scroll',
    },
  },
  placeholder: {
    color: theme?.palette?.mode === 'dark' ? 'lightgrey' : 'grey',
  },
  propertyText: {
    wordWrap: 'break-word',
  },
  labelText: {
    width: '88px',
  },
  smsField: {
    minHeight: '7em',
  },
  borderOutline: {
    borderLeft: `1px solid ${
      theme?.palette?.mode === 'dark' ? 'grey' : 'lightgrey'
    }`,
  },
  availablePropertiesContainer: {
    border: `1px solid ${
      theme?.palette?.mode === 'dark' ? 'grey' : 'lightgrey'
    }`,
    height: '180px',
    overflow: 'scroll',
  },
  selectedAvailablePropertiesContainer: {
    backgroundColor: '#0098C7',
    color: '#fff',
  },
  availablePropertyContainer: {
    padding: '2px 12px',
    cursor: 'pointer',
  },
  editButton: {
    maxWidth: '155px',
    minWidth: '155px',
    height: '36px',
  },
  hidePlaceholder: {
    '& .public-DraftEditorPlaceholder-root': {
      display: 'none !important',
    },
  },
  readonly: {
    backgroundColor: theme.palette.mode === 'dark' ? '#3a3a3a' : '#f6f6f6',
  },
  helperMargin: {
    margin: '-4px 14px 0px',
  },
}));
export function AddSmsTemplateModal(props: {
  handleClose: () => void;
  show: boolean;
  setRefetchToggleForSmsTemplates: SetterOrUpdater<Record<string, unknown>>;
  enqueueSnackbarWithStyles: (
    alertComponentProps: IAlertWithProgressBar,
    snackbarProps?: OptionsObject | undefined
  ) => SnackbarKey | null;
  currentUserBeingViewed: string;
}) {
  const classes = useStyles();
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));
  const [_, getBusinessCRMPropertiesLoadable] = makeApiRequestCached(
    {
      method: 'post',
      stage: 'itn',
      payloadJsonString: JSON.stringify({
        search: 'user',
        userId: userAtomContents?.userid,
      }),
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint: endpointEnum.BUSINESS_CRM + `/${userAtomContents?.businessid}`,
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.data ?? cached.current
  );
  const rows: {
    id: string | number;
    crm: string;
    property: string;
    label: string;
    importdatetime: string;
    selected: 0 | 1;
  }[] = useMemo(() => {
    if (getBusinessCRMPropertiesLoadable?.contents?.response?.data?.[0]) {
      try {
        let properties = JSON.parse(
          getBusinessCRMPropertiesLoadable?.contents?.response?.data?.[0]
            ?.properties
        );
        const crmName =
          getBusinessCRMPropertiesLoadable?.contents?.response?.data?.[0]
            ?.crmName ?? '';
        properties = properties.filter((elem) => elem?.name && elem?.selected);
        properties = properties.sort(
          (a, b) => (a?.displayOrder ?? -1) - (b?.displayOrder ?? -1)
        );
        properties = properties.map((elem) => {
          return {
            id: elem.id,
            crm: crmName,
            property: elem.name,
            label: elem.label,
            importdatetime: elem?.updatedDateTime ?? '',
            selected: elem?.selected ?? 0,
          };
        });
        return properties;
      } catch (e) {
        // console.log('crm properties error', e);
        return [];
      }
    }
    return [];
  }, [getBusinessCRMPropertiesLoadable]);

  const highlightStrategy = useMemo(() => {
    return (text: string, callback: any) => {
      const rowsArr = rows.map((row) => row.property);
      const regex = /(?<=^|\s)@[\w\-[\].]+(?<![.])/g;
      let m;
      while ((m = regex.exec(text)) !== null) {
        // This is necessary to avoid infinite loops with zero-width matches
        if (rowsArr.includes(m?.[0]?.slice(1))) {
          if (m.index === regex.lastIndex) {
            regex.lastIndex++;
          }
          callback(m.index, m.index + m[0].length);
        }
      }
    };
  }, [rows]);

  const addTemplateSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string()
          .required('Required')
          .max(100, 'Maximum 100 characters'),
        content: Yup.string()
          .required('Required')
          .max(512, 'Maximum 512 characters'),
      }),
    []
  );

  const crmName =
    (getBusinessCRMPropertiesLoadable?.contents?.response?.data?.[0]?.crmName?.[0]?.toUpperCase() ??
      '') +
    (getBusinessCRMPropertiesLoadable?.contents?.response?.data?.[0]?.crmName?.slice?.(
      1
    ) ?? '');

  const getLabel = (elem: any): string => {
    const lastPos = elem?.property?.lastIndexOf('.');
    if (lastPos >= 0) {
      return elem?.property?.slice(lastPos + 1) || '';
    } else {
      return elem.label;
    }
  };

  const containsText = (prop, searchText) =>
    prop?.property.toLowerCase().indexOf(searchText.toLowerCase()) > -1 ||
    prop?.label.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

  const [searchText, setSearchText] = useState('');
  const displayedOptions = useMemo(() => {
    if (!searchText || searchText == '') {
      return rows;
    } else {
      return rows.filter((prop) => containsText(prop, searchText));
    }
  }, [searchText, rows]);

  return (
    <Paper>
      <Formik
        onSubmit={async (values, actions) => {
          const { name, content } = values;
          const data = {
            call: methodNameEnum.SUBMIT_NEW_TEMPLATE,
            apikey: userAtomContents?.apikey,
            businessid: userAtomContents?.businessid,
            name,
            content,
            userid: props.currentUserBeingViewed,
          };
          const response = await mutation.mutateAsync({
            endpoint: endpointEnum.SUBMIT_NEW_TEMPLATE,
            payload: data,
          });
          props.setRefetchToggleForSmsTemplates({});
          if (response?.data?.success) {
            props.enqueueSnackbarWithStyles({
              message: 'Successfully Added SMS Template',
              alertProps: { severity: 'success' },
            });
          } else {
            props.enqueueSnackbarWithStyles({
              message: 'Encountered an error adding the SMS Template',
              alertProps: { severity: 'error' },
            });
          }
          props.handleClose();
          actions.resetForm({});
        }}
        validationSchema={addTemplateSchema}
        initialValues={{
          name: '',
          content: '',
        }}
      >
        {(formik) => {
          return (
            <>
              <form
                className="form form-label-right"
                onSubmit={() => {
                  trackSaveAddSMSTemplate();
                  formik.handleSubmit();
                }}
                noValidate
              >
                <ModalWithStyles
                  infoLabel={
                    process.env.VITE_APP_CRM_PROPERTIES === '1'
                      ? crmName
                        ? `Using ${crmName} Properties (${rows?.length ?? 0})`
                        : `Available Properties (${rows?.length ?? 0})`
                      : ''
                  }
                  onSubmit={formik.handleSubmit}
                  title="Add New SMS Template"
                  aria-labelledby="example-modal-sizes-title-lg"
                  show={props.show}
                  onHide={props.handleClose}
                  isSubmitting={formik.isSubmitting}
                  disableSubmit={!formik.dirty || !formik.isValid}
                  size="md"
                >
                  <div className="row">
                    <main
                      className={
                        process.env.VITE_APP_CRM_PROPERTIES === '1'
                          ? 'col-lg-7'
                          : 'col-lg-12'
                      }
                    >
                      <div>
                        <FormikTextField
                          name="name"
                          className={classes.field}
                          formik={formik}
                          fullWidth
                          InputProps={{
                            inputProps: {
                              maxLength: 100,
                            },
                          }}
                          label="Name"
                          size="small"
                          required
                          variant="outlined"
                        />
                      </div>
                      <div>
                        <LabelledOutline
                          label="Template *"
                          customPadding="0px"
                          error={Boolean(
                            formik.errors.content && formik.touched.content
                          )}
                        >
                          <div
                            style={{ alignItems: 'flex-start' }}
                            className={clsx(
                              classes.contentField,
                              formik.values.content
                                ? null
                                : classes.placeholder,
                              classes.hidePlaceholder
                            )}
                          >
                            {showPlaceholder && (
                              <span
                                style={{
                                  position: 'absolute',
                                  left: '12px',
                                  top: '12px',
                                  pointerEvents: 'none',
                                }}
                              >
                                Enter some text...
                              </span>
                            )}

                            <HighlightWithinTextarea
                              value={formik.values.content}
                              highlight={[
                                {
                                  highlight: highlightStrategy,
                                  className:
                                    process.env.VITE_APP_CRM_PROPERTIES === '1'
                                      ? 'property-match-text'
                                      : 'property-match-text1',
                                },
                              ]}
                              onChange={(val) =>
                                formik.setFieldValue('content', val)
                              }
                              onFocus={() => {
                                setShowPlaceholder(false);
                                formik.setFieldTouched('content', true);
                              }}
                              onBlur={() => {
                                setShowPlaceholder(!formik.values.content);
                              }}
                            />
                          </div>
                        </LabelledOutline>
                        {formik.errors.content && formik.touched.content ? (
                          <FormHelperText
                            error={true}
                            className={classes.helperMargin}
                          >
                            {formik.errors.content}
                          </FormHelperText>
                        ) : null}
                      </div>
                    </main>
                    {process.env.VITE_APP_CRM_PROPERTIES === '1' && (
                      <main
                        className={clsx(classes.borderOutline, 'col-lg-5')}
                        style={{ paddingRight: '0px' }}
                      >
                        <div
                          className={classes.availablePropertiesContainer}
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            padding: '0px 0px',
                            height: '210px',
                            marginTop: '8px',
                          }}
                        >
                          <FormControl fullWidth>
                            <List component="nav" dense={true}>
                              <ListSubheader style={{ padding: '0px 5px' }}>
                                <TextField
                                  // Autofocus on textfield
                                  autoFocus
                                  size="small"
                                  placeholder="Type to search..."
                                  fullWidth
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <SearchIcon />
                                      </InputAdornment>
                                    ),
                                  }}
                                  // Need to specify a white background or LisItems will
                                  // show under the TextFiled when you scroll the list.
                                  style={{ backgroundColor: 'white' }}
                                  onChange={(e) =>
                                    setSearchText(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key !== 'Escape') {
                                      // Prevents autoselecting item while typing (default Select behaviour)
                                      e.stopPropagation();
                                    }
                                  }}
                                />
                              </ListSubheader>
                              {displayedOptions.map((prop) => (
                                <ListItem
                                  key={prop?.property}
                                  button={true}
                                  onClick={() => {
                                    formik.setFieldValue(
                                      'content',
                                      formik.values.content +
                                        '@' +
                                        prop?.property +
                                        ' '
                                    );
                                  }}
                                >
                                  <ListItemText
                                    className={classes.propertyText}
                                  >
                                    {'@' + prop?.property}
                                  </ListItemText>
                                  <Typography
                                    className={classes.labelText}
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    {getLabel(prop)}
                                  </Typography>
                                </ListItem>
                              ))}
                            </List>
                          </FormControl>
                        </div>
                      </main>
                    )}
                  </div>
                </ModalWithStyles>
              </form>
            </>
          );
        }}
      </Formik>
    </Paper>
  );
}
